import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Search, X, ArrowLeft, Calendar, MessageSquare, BookOpen, Feather, Loader2, ChevronDown, ChevronUp, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function highlightText(text, query) {
  if (!query) return text;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={`hl-${i}-${part}`} style={{ background: 'rgba(196,149,106,0.3)', color: '#f5f0e8', borderRadius: '2px', padding: '0 2px' }}>{part}</mark>
      : part
  );
}

function BiographyCard({ bio }) {
  const ws = new Date(bio.week_start + "T00:00:00");
  const we = new Date(bio.week_end + "T00:00:00");
  const label = `${ws.toLocaleDateString("en-US", { month: "short", day: "numeric" })} \u2013 ${we.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <div data-testid={`biography-${bio.id}`} className="biography-card" style={{
      background: 'rgba(196,149,106,0.04)',
      border: '1px solid rgba(196,149,106,0.15)',
      borderRadius: '12px',
      padding: '1.75rem',
      position: 'relative'
    }}>
      <div className="flex items-center gap-2 mb-4">
        <Feather size={14} style={{ color: '#c4956a' }} />
        <span style={{ color: '#c4956a', fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>Weekly Reflection</span>
      </div>
      <p style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: '1.05rem',
        lineHeight: 1.8,
        color: '#e0dbd4'
      }}>{bio.content}</p>
      <div className="flex items-center justify-between mt-4 pt-3" style={{ borderTop: '1px solid rgba(196,149,106,0.1)' }}>
        <span style={{ color: '#6a6560', fontSize: '0.75rem' }}>{label}</span>
        <span style={{ color: '#6a6560', fontSize: '0.75rem' }}>{bio.session_count} conversation{bio.session_count !== 1 ? 's' : ''}</span>
      </div>
    </div>
  );
}

function WeekButton({ week, onGenerate, generating }) {
  const ws = new Date(week.week_start + "T00:00:00");
  const we = new Date(week.week_end + "T00:00:00");
  const label = `${ws.toLocaleDateString("en-US", { month: "short", day: "numeric" })} \u2013 ${we.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;

  if (week.has_biography) return null;

  return (
    <button
      data-testid={`generate-bio-${week.week_start}`}
      onClick={() => onGenerate(week.week_start)}
      disabled={generating}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.5rem 1rem', background: 'transparent',
        border: '1px solid #2e2a26', borderRadius: '50px',
        color: '#c4956a', fontSize: '0.8rem', cursor: 'pointer',
        fontFamily: "'Outfit', sans-serif",
        transition: 'border-color 0.2s',
        opacity: generating ? 0.6 : 1
      }}
      onMouseEnter={e => e.target.style.borderColor = '#c4956a'}
      onMouseLeave={e => e.target.style.borderColor = '#2e2a26'}
    >
      {generating ? <Loader2 size={12} className="animate-spin" /> : <Feather size={12} />}
      Write {label}
    </button>
  );
}

function SessionCard({ session, searchQuery, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const msgs = session.messages || [];
  const previewMsgs = msgs.slice(0, 4);
  const remaining = msgs.length - 4;
  const displayMsgs = expanded ? msgs : previewMsgs;
  const hasMatch = searchQuery && msgs.some(m => m.content.toLowerCase().includes(searchQuery.toLowerCase()));

  const date = new Date(session.date + "T00:00:00");
  const dateStr = date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <div data-testid={`session-card-${session.id}`} className={`timeline-card ${hasMatch ? 'has-match' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar size={14} style={{ color: '#c4956a' }} />
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '0.9rem', color: '#c4956a' }}>{dateStr}</span>
        </div>
        <span style={{ color: '#6a6560', fontSize: '0.75rem' }}>{msgs.length} messages</span>
      </div>
      <div className="space-y-3">
        {displayMsgs.map((msg, i) => (
          <div key={`${session.id}-${msg.role}-${msg.timestamp || i}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={msg.role === "echo" ? "chat-bubble-echo" : "chat-bubble-user"} style={{ fontSize: '0.85rem', maxWidth: '90%' }}>
              {searchQuery ? highlightText(msg.content, searchQuery) : msg.content}
            </div>
          </div>
        ))}
      </div>
      {!expanded && remaining > 0 && (
        <button
          onClick={() => setExpanded(true)}
          className="flex items-center gap-1 mt-3"
          style={{ color: '#c4956a', fontSize: '0.8rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}
        >
          <ChevronDown size={14} /> {remaining} more message{remaining > 1 ? 's' : ''}
        </button>
      )}
      {expanded && msgs.length > 4 && (
        <button
          onClick={() => setExpanded(false)}
          className="flex items-center gap-1 mt-3"
          style={{ color: '#6a6560', fontSize: '0.8rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}
        >
          <ChevronUp size={14} /> Show less
        </button>
      )}
    </div>
  );
}

export default function TimelinePage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [biographies, setBiographies] = useState([]);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [generatingWeek, setGeneratingWeek] = useState(null);
  const [activeTab, setActiveTab] = useState("all"); // "all" | "reflections" | "conversations"
  const [moodData, setMoodData] = useState({ mood_timeline: [], insights: [] });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sessRes, bioRes, weeksRes, moodRes] = await Promise.all([
          axios.get(`${API}/api/chat/sessions`, { withCredentials: true }),
          axios.get(`${API}/api/biography/entries`, { withCredentials: true }),
          axios.get(`${API}/api/biography/available-weeks`, { withCredentials: true }),
          axios.get(`${API}/api/mood/insights`, { withCredentials: true })
        ]);
        setSessions(sessRes.data);
        setBiographies(bioRes.data);
        setAvailableWeeks(weeksRes.data);
        setMoodData(moodRes.data);
      } catch (err) {
        /* Data fetch failed — empty state will show */
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/export/memoir`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'echo_memoir.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      /* Export failed — button re-enables on finally */
    } finally {
      setExporting(false);
    }
  };

  const handleGenerateBio = async (weekStart) => {
    setGeneratingWeek(weekStart);
    try {
      // Calculate week offset
      const today = new Date();
      const startOfThisWeek = new Date(today);
      startOfThisWeek.setDate(today.getDate() - today.getDay() + 1);
      const targetWeek = new Date(weekStart + "T00:00:00");
      const diffMs = startOfThisWeek.getTime() - targetWeek.getTime();
      const weekOffset = Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));

      const res = await axios.post(`${API}/api/biography/generate`, { week_offset: Math.max(0, weekOffset) }, { withCredentials: true });
      setBiographies(prev => [res.data, ...prev]);
      setAvailableWeeks(prev => prev.map(w => w.week_start === weekStart ? { ...w, has_biography: true } : w));
    } catch (err) {
      /* Biography generation failed — button re-enables on finally */
    } finally {
      setGeneratingWeek(null);
    }
  };

  const filteredSessions = useMemo(() => {
    if (!searchQuery) return sessions;
    return sessions.filter(s =>
      s.messages?.some(m => m.content.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [sessions, searchQuery]);

  const grouped = useMemo(() => {
    const groups = {};
    filteredSessions.forEach(s => {
      const date = new Date(s.date + "T00:00:00");
      const key = date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
      if (!groups[key]) groups[key] = [];
      groups[key].push(s);
    });
    return groups;
  }, [filteredSessions]);

  const totalEntries = sessions.length;
  const totalMessages = sessions.reduce((acc, s) => acc + (s.messages?.length || 0), 0);
  const totalReflections = biographies.length;
  const unwrittenWeeks = availableWeeks.filter(w => !w.has_biography);

  return (
    <div data-testid="timeline-page" className="min-h-screen" style={{ background: '#141210' }}>
      <nav className="echo-nav">
        <div className="flex items-center gap-4">
          <Link to="/journal" style={{ color: '#9a9590', textDecoration: 'none' }}><ArrowLeft size={18} /></Link>
          <span className="echo-logo">ECHO</span>
        </div>
        <div className="flex items-center gap-2">
          <BookOpen size={16} style={{ color: '#c4956a' }} />
          <span style={{ color: '#c4956a', fontSize: '0.85rem', fontFamily: "'Outfit', sans-serif" }}>Archive</span>
        </div>
      </nav>

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              { label: "Entries", value: totalEntries, icon: <MessageSquare size={14} /> },
              { label: "Messages", value: totalMessages, icon: <BookOpen size={14} /> },
              { label: "Reflections", value: totalReflections, icon: <Feather size={14} /> }
            ].map((stat) => (
              <div key={stat.label} data-testid={`stat-${stat.label.toLowerCase()}`} className="text-center py-4" style={{ background: 'rgba(26,24,20,0.6)', border: '1px solid #2e2a26', borderRadius: '8px' }}>
                <div className="flex items-center justify-center gap-1 mb-1" style={{ color: '#c4956a' }}>{stat.icon}</div>
                <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.25rem', color: '#f5f0e8' }}>{stat.value}</p>
                <p style={{ color: '#6a6560', fontSize: '0.7rem' }}>{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Export + Mood Insights row */}
          <div className="flex items-center justify-between mb-6">
            {/* Mood insight */}
            <div className="flex-1">
              {moodData.insights.length > 0 && (
                <p data-testid="mood-insight" style={{
                  color: '#9a9590', fontSize: '0.8rem', fontFamily: "'Outfit', sans-serif"
                }}>
                  {moodData.insights[0]}
                </p>
              )}
            </div>
            {/* Export button */}
            <button
              data-testid="export-memoir-btn"
              onClick={handleExport}
              disabled={exporting || totalEntries === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-full transition-all"
              style={{
                background: 'rgba(196,149,106,0.08)',
                border: '1px solid rgba(196,149,106,0.2)',
                color: '#c4956a',
                fontSize: '0.8rem',
                fontFamily: "'Outfit', sans-serif",
                cursor: (exporting || totalEntries === 0) ? 'default' : 'pointer',
                opacity: (exporting || totalEntries === 0) ? 0.5 : 1
              }}
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {exporting ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>

          {/* Mood timeline */}
          {moodData.mood_timeline.length >= 3 && (
            <div data-testid="mood-timeline" className="mb-8 p-4" style={{
              background: 'rgba(26,24,20,0.6)', border: '1px solid #2e2a26', borderRadius: '10px'
            }}>
              <p style={{ color: '#6a6560', fontSize: '0.7rem', fontFamily: "'Outfit', sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                How you've been
              </p>
              <div className="flex items-end gap-1" style={{ height: '40px' }}>
                {moodData.mood_timeline.slice(-14).map((m, i) => {
                  const moodColors = {
                    upbeat: '#c4956a', excited: '#d4a574', grateful: '#b8a080',
                    calm: '#8a9a8a', reflective: '#7a8a9a', neutral: '#6a6560',
                    stressed: '#9a7060', anxious: '#a07060', frustrated: '#a06050', low: '#605550'
                  };
                  const color = moodColors[m.mood] || '#6a6560';
                  const heights = { upbeat: '100%', excited: '95%', grateful: '85%', calm: '70%', reflective: '60%', neutral: '50%', stressed: '40%', anxious: '35%', frustrated: '30%', low: '20%' };
                  const h = heights[m.mood] || '50%';
                  return (
                    <div key={`mood-${m.date}-${i}`} className="flex-1 rounded-sm transition-all" title={`${m.date}: ${m.mood}`}
                      style={{ background: color, height: h, minWidth: '4px', opacity: 0.7 }} />
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span style={{ color: '#3a3530', fontSize: '0.6rem' }}>
                  {moodData.mood_timeline.slice(-14)[0]?.date}
                </span>
                <span style={{ color: '#3a3530', fontSize: '0.6rem' }}>
                  {moodData.mood_timeline.slice(-14).pop()?.date}
                </span>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 mb-6 p-1" style={{ background: 'rgba(26,24,20,0.8)', borderRadius: '8px', border: '1px solid #2e2a26' }}>
            {[
              { key: "all", label: "All" },
              { key: "reflections", label: "Reflections" },
              { key: "conversations", label: "Conversations" }
            ].map(tab => (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className="flex-1 py-2 text-sm rounded-md transition-all"
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  background: activeTab === tab.key ? 'rgba(196,149,106,0.1)' : 'transparent',
                  color: activeTab === tab.key ? '#c4956a' : '#6a6560',
                  border: 'none', cursor: 'pointer'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Generate biography buttons */}
          {(activeTab === "all" || activeTab === "reflections") && unwrittenWeeks.length > 0 && (
            <div data-testid="biography-generator" className="mb-8 p-5" style={{ background: 'rgba(196,149,106,0.03)', border: '1px solid rgba(196,149,106,0.1)', borderRadius: '12px' }}>
              <div className="flex items-center gap-2 mb-3">
                <Feather size={14} style={{ color: '#c4956a' }} />
                <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, color: '#f5f0e8', fontSize: '0.9rem' }}>Write your week</span>
              </div>
              <p style={{ color: '#9a9590', fontSize: '0.8rem', marginBottom: '1rem', lineHeight: 1.6 }}>
                ECHO can write a biography paragraph from your conversations. A few sentences that capture how the week felt.
              </p>
              <div className="flex flex-wrap gap-2">
                {unwrittenWeeks.slice(0, 4).map(week => (
                  <WeekButton
                    key={week.week_start}
                    week={week}
                    onGenerate={handleGenerateBio}
                    generating={generatingWeek === week.week_start}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Search (only for conversations) */}
          {(activeTab === "all" || activeTab === "conversations") && (
            <div className="relative mb-6">
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6a6560' }} />
              <input
                data-testid="search-input"
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search your memories..."
                className="echo-input"
                style={{ paddingLeft: '36px', paddingRight: searchQuery ? '36px' : '12px' }}
              />
              {searchQuery && (
                <button
                  data-testid="search-clear-btn"
                  onClick={() => setSearchQuery("")}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6a6560', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}

          {searchQuery && (
            <p className="mb-4" style={{ color: '#6a6560', fontSize: '0.8rem' }}>
              {filteredSessions.length} result{filteredSessions.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
          )}

          {/* Content */}
          {loading ? (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: '#c4956a', borderTopColor: 'transparent' }} />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Reflections */}
              {(activeTab === "all" || activeTab === "reflections") && biographies.length > 0 && (
                <div className="space-y-4">
                  {activeTab === "all" && (
                    <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#c4956a', fontSize: '1rem', letterSpacing: '0.05em' }}>Reflections</h3>
                  )}
                  {biographies.map(bio => (
                    <BiographyCard key={bio.id} bio={bio} />
                  ))}
                </div>
              )}

              {/* Conversations */}
              {(activeTab === "all" || activeTab === "conversations") && (
                <>
                  {Object.keys(grouped).length === 0 ? (
                    <div className="text-center py-16">
                      <p style={{ fontFamily: "'Playfair Display', serif", color: '#9a9590', fontSize: '1rem' }}>
                        {searchQuery ? "No memories match your search." : "Your archive is empty. Start a conversation to begin."}
                      </p>
                      {!searchQuery && (
                        <Link to="/journal" className="btn-amber inline-flex mt-6">Start journaling</Link>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-8">
                      {activeTab === "all" && sessions.length > 0 && (
                        <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#c4956a', fontSize: '1rem', letterSpacing: '0.05em' }}>Conversations</h3>
                      )}
                      {Object.entries(grouped).map(([month, monthSessions]) => (
                        <div key={month}>
                          <h3 className="mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#9a9590', fontSize: '0.9rem', letterSpacing: '0.05em' }}>{month}</h3>
                          <div className="space-y-4">
                            {monthSessions.map(session => (
                              <SessionCard
                                key={session.id}
                                session={session}
                                searchQuery={searchQuery}
                                defaultExpanded={!!searchQuery}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Empty reflections state */}
              {activeTab === "reflections" && biographies.length === 0 && (
                <div className="text-center py-16">
                  <Feather size={24} style={{ color: '#3a3530', margin: '0 auto 1rem' }} />
                  <p style={{ fontFamily: "'Playfair Display', serif", color: '#9a9590', fontSize: '1rem', marginBottom: '0.5rem' }}>
                    No reflections yet.
                  </p>
                  <p style={{ color: '#6a6560', fontSize: '0.85rem' }}>
                    Have a few conversations, then come back and write your week.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
